import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesAPI, jobDescriptionAPI } from '../services/api';
import { handleActivateKey } from '../utils/a11y';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge, { StatusBadge } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import HelpTooltip from '../components/ui/HelpTooltip';
import { useAuth } from '../context/AuthContext';
import PersonalComplianceWidget, {
  ProgressBar,
} from '../components/dashboard/PersonalComplianceWidget';
import JobDescriptionFlowCards from '../components/JobDescriptionFlowCards';

// Progres pe module dintr-un training (module completate / total). Aceeași regulă ca backend-ul:
// un modul e „done" dacă e completed / visited / percentage>=100. PASSED = 100%.
const getModuleProgress = (training, modules) => {
  const total = (modules || []).length;
  if (!training) return { completed: 0, total, percent: 0 };
  if (training.status === 'PASSED' || training.passed)
    return { completed: total, total, percent: 100 };
  const vp = training.videoProgress || {};
  let completed = 0;
  for (const m of modules || []) {
    const mp = vp[m.id];
    if (
      mp &&
      (mp.completed === true ||
        mp.visited === true ||
        (typeof mp.percentage === 'number' && mp.percentage >= 100))
    )
      completed += 1;
  }
  const percent = total > 0 ? Math.round((completed / total) * 100) : training.progress || 0;
  return { completed, total, percent };
};

// Ordinea legală a instruirilor inițiale
const ONBOARDING_ORDER = ['IIG_SSM', 'ILM_SSM', 'IIG_SU', 'ILM_SU'];

// Ordinea canonică pentru instruirile periodice/suplimentare (după onboarding): toate SSM înaintea
// SU. La fel de tip → cele atribuite mai devreme primele. Așa cursantul cu IP SSM + IP SU le vede
// (și le face) în ordine: întâi tot SSM, apoi SU.
const OTHER_ORDER = { IP_SSM: 0, IP_SU: 1, SUPLIMENTARE_SSM: 2, SUPLIMENTARE_SU: 3 };

// Fiecare tip → cheia i18n a etichetei complete. Traducerea se face la afișare (t(TYPE_LABELS[type])).
const TYPE_LABELS = {
  IIG_SSM: 'myCourses.typeLabels.iigSsm',
  ILM_SSM: 'myCourses.typeLabels.ilmSsm',
  IIG_SU: 'myCourses.typeLabels.iigSu',
  ILM_SU: 'myCourses.typeLabels.ilmSu',
  IP_SSM: 'myCourses.typeLabels.ipSsm',
  IP_SU: 'myCourses.typeLabels.ipSu',
  SUPLIMENTARE_SSM: 'myCourses.typeLabels.supplementarySsm',
  SUPLIMENTARE_SU: 'myCourses.typeLabels.supplementarySu',
};

// Acronimele (IIG/ILM/IP) sunt identice în ambele limbi; doar „Suplimentară" e tradusă la afișare.
const TYPE_SHORT = {
  IIG_SSM: 'IIG',
  ILM_SSM: 'ILM',
  IIG_SU: 'IIG',
  ILM_SU: 'ILM',
  IP_SSM: 'IP',
  IP_SU: 'IP',
  SUPLIMENTARE_SSM: 'SUPLIMENTARE_SSM',
  SUPLIMENTARE_SU: 'SUPLIMENTARE_SU',
};

// Eticheta scurtă pentru badge: acronim literal, sau „Suplimentară" tradusă.
const typeShortLabel = (type, t) =>
  type === 'SUPLIMENTARE_SSM' || type === 'SUPLIMENTARE_SU'
    ? t('myCourses.typeShort.supplementary')
    : TYPE_SHORT[type];

const isSSMType = (type) => type?.endsWith('_SSM');
const isSUType = (type) => type?.endsWith('_SU');
const isOnboardingType = (type) => ONBOARDING_ORDER.includes(type);

const MyCourses = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCongrats, setShowCongrats] = useState(false);
  // Flow fișa postului — fetch-uit o singură dată la nivelul paginii, pasat la OnboardingGroup
  // ca step inline (al N+1-lea card în șir, după IIG/ILM corespunzător). Materializare lazy
  // (backend creează instance când angajatul atinge pasul, dacă există template).
  const [jobDescFlow, setJobDescFlow] = useState({ ssm: null, su: null });

  useEffect(() => {
    fetchMyCourses();
    fetchJobDescFlow();
  }, []);

  const fetchJobDescFlow = async () => {
    try {
      const { data } = await jobDescriptionAPI.getMyFlow();
      setJobDescFlow(data || { ssm: null, su: null });
    } catch (_) {
      /* silent — feature opțional */
    }
  };

  const fetchMyCourses = async () => {
    try {
      const response = await coursesAPI.getMyCourses();
      setCourses(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Separă cursurile în: onboarding (IIG/ILM) și periodice/suplimentare
  const onboardingCourses = courses.filter((a) => isOnboardingType(a.course.type));
  // Periodice/suplimentare sortate canonic: IP SSM → IP SU → suplimentare; la fel de tip, după
  // assignedAt (cele mai vechi primele). Astfel ordinea nu mai e amestecată când se trimit mai multe.
  const otherCourses = courses
    .filter((a) => !isOnboardingType(a.course.type))
    .sort((a, b) => {
      const r = (OTHER_ORDER[a.course.type] ?? 9) - (OTHER_ORDER[b.course.type] ?? 9);
      if (r !== 0) return r;
      const ta = a.assignedAt ? new Date(a.assignedAt).getTime() : 0;
      const tb = b.assignedAt ? new Date(b.assignedAt).getTime() : 0;
      return ta - tb;
    });

  // Sortează onboarding după ordine legală
  const sortedOnboarding = [...onboardingCourses].sort((a, b) => {
    return ONBOARDING_ORDER.indexOf(a.course.type) - ONBOARDING_ORDER.indexOf(b.course.type);
  });

  // Grupează onboarding pe serviciu (SSM / SU)
  const ssmOnboarding = sortedOnboarding.filter((a) => isSSMType(a.course.type));
  const suOnboarding = sortedOnboarding.filter((a) => isSUType(a.course.type));

  // #4: detectează finalizarea instruirilor INIȚIALE grupate (toate PASSED + semnate de angajat).
  // La prima vizită după finalizare arătăm un pop-up de felicitare (o singură dată per browser).
  const groupComplete = (group) =>
    group.length > 0 &&
    group.every(
      (a) =>
        a.training?.status === 'PASSED' &&
        a.training?.signatures?.some((s) => s.signerRole === 'EMPLOYEE')
    );
  const onboardingPresent = ssmOnboarding.length > 0 || suOnboarding.length > 0;
  const allOnboardingComplete =
    onboardingPresent &&
    (ssmOnboarding.length === 0 || groupComplete(ssmOnboarding)) &&
    (suOnboarding.length === 0 || groupComplete(suOnboarding));
  const hasIP = otherCourses.some((a) => a.course.type === 'IP_SSM' || a.course.type === 'IP_SU');

  useEffect(() => {
    if (loading || !allOnboardingComplete) return;
    const key = `onboardingCongrats_${user?.id || 'me'}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* ignore */
    }
    setShowCongrats(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, allOnboardingComplete]);

  // Verifică dacă un curs e deblocat (poate fi început)
  const isUnlocked = (assignment) => {
    const type = assignment.course.type;
    const idx = ONBOARDING_ORDER.indexOf(type);
    if (idx <= 0) return true; // IIG SSM e mereu deblocat

    // Verifică dacă toate cele dinainte sunt PASSED
    for (let i = 0; i < idx; i++) {
      const prevType = ONBOARDING_ORDER[i];
      const prevAssignment = courses.find((a) => a.course.type === prevType);
      if (!prevAssignment) continue; // Nu are acest tip, skip (ex: nu are SU)
      if (prevAssignment.training?.status !== 'PASSED') return false;
    }
    return true;
  };

  // Pentru IP/SUPLIMENTARE: deblocat doar când IIG+ILM (același domeniu) sunt COMPLET SEMNATE
  // (toate slot-urile din matrice). Aliniat cu blocajul backend din `startTraining` care
  // refuză startul cu HTTP 409 dacă pre-requisite-le nu sunt îndeplinite. Folosim flag-ul
  // `training._fullySigned` calculat backend-side în `getMyCourses`.
  // Excepție: COMPANY_ADMIN ca trainee nu face ILM (matricea returnează null), deci pentru
  // el pre-requisite pentru IP e DOAR IIG.
  const isILMApplicableForTrainee = () => {
    const traineeRole = user?.role || 'EMPLOYEE';
    return traineeRole !== 'COMPANY_ADMIN';
  };
  // Regulă owner: dacă există IIG/ILM atribuite, IP-ul vine DUPĂ ele (rămâne blocat până sunt
  // semnate → fluxul cursiv IIG→ILM→FP→IP se păstrează). Dacă IIG/ILM NU sunt atribuite, IP-ul e
  // independent (deblocat). Deci blocăm DOAR pe IIG/ILM care EXISTĂ și nu-s semnate complet.
  const isOtherCourseUnlocked = (assignment) => {
    const type = assignment.course.type;
    const isSU = type.endsWith('_SU');
    const isIPLike = type.startsWith('IP_') || type.startsWith('SUPLIMENTARE_');
    if (!isIPLike) return true;
    const iigType = isSU ? 'IIG_SU' : 'IIG_SSM';
    const ilmType = isSU ? 'ILM_SU' : 'ILM_SSM';
    const iig = courses.find((a) => a.course?.type === iigType);
    if (iig && !iig.training?._fullySigned) return false; // IIG atribuit dar nesemnat → IP blocat
    if (isILMApplicableForTrainee()) {
      const ilm = courses.find((a) => a.course?.type === ilmType);
      if (ilm && !ilm.training?._fullySigned) return false; // ILM atribuit dar nesemnat → IP blocat
    }
    return true; // IIG/ILM lipsesc sau sunt semnate → IP disponibil
  };
  const getOtherCourseBlockers = (assignment) => {
    const type = assignment.course.type;
    const isSU = type.endsWith('_SU');
    const blockers = [];
    const iigType = isSU ? 'IIG_SU' : 'IIG_SSM';
    const ilmType = isSU ? 'ILM_SU' : 'ILM_SSM';
    const iig = courses.find((a) => a.course?.type === iigType);
    if (iig && !iig.training?._fullySigned)
      blockers.push(t('myCourses.notFullySigned', { name: iigType.replace('_', ' ') }));
    if (isILMApplicableForTrainee()) {
      const ilm = courses.find((a) => a.course?.type === ilmType);
      if (ilm && !ilm.training?._fullySigned)
        blockers.push(t('myCourses.notFullySigned', { name: ilmType.replace('_', ' ') }));
    }
    return blockers;
  };

  // Ce urmează după acest curs (pentru butonul "Continuă")
  const getNextCourse = (assignment) => {
    const type = assignment.course.type;
    const idx = ONBOARDING_ORDER.indexOf(type);
    if (idx < 0 || idx >= ONBOARDING_ORDER.length - 1) return null;

    for (let i = idx + 1; i < ONBOARDING_ORDER.length; i++) {
      const nextType = ONBOARDING_ORDER[i];
      const nextAssignment = courses.find((a) => a.course.type === nextType);
      if (nextAssignment) return nextAssignment;
    }
    return null;
  };

  if (loading) return <Spinner size="lg" label={t('common.loading')} />;

  if (courses.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('myCourses.title')} subtitle={t('myCourses.subtitle')} />
        <EmptyState
          icon={
            <svg
              className="w-12 h-12 text-slate-300 dark:text-slate-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          }
          title={t('myCourses.noCourses')}
          description={t('myCourses.noCoursesHint')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* #4: pop-up de felicitare la finalizarea instruirilor inițiale grupate (+ anunț IP-uri dacă există). */}
      <Modal
        open={showCongrats}
        onClose={() => setShowCongrats(false)}
        title={t('myCourses.congratsTitle')}
        footer={
          <div className="flex justify-end">
            <Button variant="accent" onClick={() => setShowCongrats(false)}>
              {t('myCourses.congratsOk')}
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          {/* Adminul nu are ILM atribuit — textul „IIG și ILM" e derutant pentru el. Selectăm
              cheia după tipurile efectiv prezente în onboardingCourses, ca să nu menționăm
              ceva ce userul n-a parcurs. */}
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {(() => {
              const hasIIG = onboardingCourses.some((a) => a.course.type.startsWith('IIG_'));
              const hasILM = onboardingCourses.some((a) => a.course.type.startsWith('ILM_'));
              if (hasIIG && hasILM) return t('myCourses.congratsBody');
              if (hasIIG) return t('myCourses.congratsBodyIIG');
              return t('myCourses.congratsBodyILM');
            })()}
          </p>
          {hasIP && (
            <p className="text-sm text-amber-700 dark:text-amber-300">
              {t('myCourses.congratsIP')}
            </p>
          )}
        </div>
      </Modal>

      <PageHeader title={t('myCourses.title')} subtitle={t('myCourses.subtitle')} />

      {/* #27: punct de glosar pentru prescurtări — tap pe „?" explică IIG/ILM/IP/SSM/SU. */}
      <div className="-mt-3 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <span>{t('myCourses.glossaryPrompt')}</span>
        <HelpTooltip text={t('myCourses.glossary')} />
      </div>

      {/* Widget „Următorul pas" + semafor personal — vizibilitate clară a acțiunii imediate.
          Se auto-ascunde dacă nu e nimic de monitorizat. Refolosit din Dashboard. */}
      <PersonalComplianceWidget />

      {/* Instruiri inițiale (IIG + ILM) — grupate pe serviciu.
          Fișa postului apare ca step inline al grupului corespunzător (după ILM_SSM / ILM_SU
          sau IIG dacă angajatul e admin trainee), nu ca block separat. */}
      {ssmOnboarding.length > 0 && (
        <OnboardingGroup
          label={t('myCourses.ssmGroup')}
          color="blue"
          assignments={ssmOnboarding}
          isUnlocked={isUnlocked}
          getNextCourse={getNextCourse}
          navigate={navigate}
          t={t}
          jobDescriptionStep={jobDescFlow?.ssm}
        />
      )}

      {suOnboarding.length > 0 && (
        <OnboardingGroup
          label={t('myCourses.suGroup')}
          color="red"
          assignments={suOnboarding}
          isUnlocked={isUnlocked}
          getNextCourse={getNextCourse}
          navigate={navigate}
          t={t}
          jobDescriptionStep={jobDescFlow?.su}
        />
      )}

      {/* Fișa postului STANDALONE — pentru domeniile fără grup de onboarding (ex: cursant/firmă
          doar-IP). Când există IIG/ILM, FP apare ca step în OnboardingGroup; aici acoperim cazul în
          care nu există niciun grup, ca FP-ul materializat să nu rămână invizibil. Se auto-ascunde
          dacă nu e nimic acționabil pentru domeniul respectiv. */}
      {ssmOnboarding.length === 0 && <JobDescriptionFlowCards filterDomain="SSM" standalone />}
      {suOnboarding.length === 0 && <JobDescriptionFlowCards filterDomain="SU" standalone />}

      {/* Instruiri periodice și suplimentare */}
      {otherCourses.length > 0 && (
        <>
          <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-2">
            {t('myCourses.periodicTrainings')}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {otherCourses.map((assignment) => (
              <CourseCard
                key={assignment.id}
                assignment={assignment}
                unlocked={isOtherCourseUnlocked(assignment)}
                blockers={getOtherCourseBlockers(assignment)}
                navigate={navigate}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Grup de onboarding (SSM sau SU) — stepper vizual.
// `jobDescriptionStep` (opțional) e adăugat ca step final după cursuri — vizibilă doar dacă
// prereq-ul (ILM/IIG) e complet. Backend (getMyFlow) determină când e disponibilă.
const OnboardingGroup = ({
  label,
  color,
  assignments,
  isUnlocked,
  getNextCourse,
  _navigate,
  t,
  jobDescriptionStep,
}) => {
  const allDone = assignments.every((a) => a.training?.status === 'PASSED');
  const allSigned = assignments.every((a) =>
    a.training?.signatures?.some((s) => s.signerRole === 'EMPLOYEE')
  );
  // Cardul fișei postului apare doar pentru status-uri „acționabile" — WAITING_PREREQUISITE
  // și NO_PARENT_SSM sunt ascunse (angajatul nu poate face nimic).
  const jdVisible =
    jobDescriptionStep &&
    ['WAITING_EMPLOYEE', 'WAITING_COUNTERPART', 'FULLY_SIGNED', 'TEMPLATE_MISSING'].includes(
      jobDescriptionStep.status
    );
  const jdSigned = jdVisible && jobDescriptionStep.status === 'FULLY_SIGNED';
  const isFullyComplete = allDone && allSigned && (!jdVisible || jdSigned);
  const [expanded, setExpanded] = useState(!isFullyComplete);

  // Light mode = original vivid (primary-600 / danger-600). Dark mode = pastel soft.
  const barBg =
    color === 'blue'
      ? 'bg-primary-600 dark:bg-primary-900/30 dark:border-b dark:border-primary-900/50'
      : 'bg-danger-600 dark:bg-warning-900/30 dark:border-b dark:border-warning-900/50';
  const barText =
    color === 'blue' ? 'text-white dark:text-primary-200' : 'text-white dark:text-warning-200';
  const iconColor =
    color === 'blue' ? 'text-white dark:text-primary-300' : 'text-white dark:text-warning-300';

  // Dacă totul e completat și semnat — arată doar bara cu rezumat
  if (isFullyComplete && !expanded) {
    return (
      <div className="rounded-xl border-2 border-accent-200 dark:border dark:border-accent-800 overflow-hidden dark:bg-accent-950/30">
        <button
          type="button"
          aria-label={t('myCourses.expandCourse')}
          onClick={() => setExpanded(true)}
          className={`${barBg} bg-opacity-60 dark:bg-opacity-100 px-4 py-2.5 flex items-center justify-between w-full text-left`}
        >
          <div className="flex items-center gap-2">
            <svg
              className={`w-5 h-5 ${iconColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className={`${barText} font-semibold text-sm`}>{label}</span>
            <Badge color="green" className="text-xs">
              {t('myCourses.completed')}
            </Badge>
          </div>
          <svg
            className={`w-4 h-4 ${color === 'blue' ? 'text-white/70 dark:text-primary-300/70' : 'text-white/70 dark:text-warning-300/70'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    );
  }

  const outerBorder = allDone
    ? 'border-accent-200 dark:border-accent-800'
    : color === 'blue'
      ? 'border-primary-200 dark:border-primary-900/60'
      : 'border-danger-200 dark:border-warning-900/60';

  return (
    <div className={`rounded-xl border-2 overflow-hidden dark:bg-slate-800 ${outerBorder}`}>
      {/* Bară: vivid în light, pastel în dark */}
      <div
        className={`${barBg} px-4 py-2.5 flex items-center justify-between ${isFullyComplete ? 'cursor-pointer' : ''}`}
        {...(isFullyComplete
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': t('myCourses.collapseCourse'),
              onClick: () => setExpanded(false),
              onKeyDown: (e) => handleActivateKey(e, () => setExpanded(false)),
            }
          : {})}
      >
        <div className="flex items-center gap-2">
          {color === 'blue' ? (
            <svg
              className={`w-5 h-5 ${iconColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          ) : (
            <svg
              className={`w-5 h-5 ${iconColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          )}
          <span className={`${barText} font-semibold text-sm`}>{label}</span>
        </div>
        {allDone && (
          <Badge color="green" className="text-xs">
            {t('myCourses.completed')}
          </Badge>
        )}
      </div>

      {/* Stepper: cursuri în ordine */}
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {assignments.map((assignment, idx) => {
          const unlocked = isUnlocked(assignment);
          const training = assignment.training;
          const isPassed = training?.status === 'PASSED';
          const isFailed = training?.status === 'FAILED';
          const isInProgress = training?.status === 'IN_PROGRESS';
          const hasSigned = training?.signatures?.some((s) => s.signerRole === 'EMPLOYEE');
          const nextCourse = isPassed ? getNextCourse(assignment) : null;
          const type = assignment.course.type;
          const trainingLink = `/training/${assignment.course.id}`;

          return (
            <div key={assignment.id} className={`p-4 ${!unlocked ? 'opacity-50' : ''}`}>
              <div className="flex items-start gap-4">
                {/* Step indicator */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      isPassed
                        ? 'bg-accent-100 dark:bg-accent-950/40 text-accent-700 dark:text-accent-300'
                        : isInProgress
                          ? `${color === 'blue' ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300' : 'bg-danger-100 dark:bg-warning-900/40 text-danger-700 dark:text-warning-300'} ring-2 ${color === 'blue' ? 'ring-primary-300 dark:ring-primary-800' : 'ring-danger-300 dark:ring-warning-800'}`
                          : unlocked
                            ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-300 dark:text-slate-600'
                    }`}
                  >
                    {isPassed ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : !unlocked ? (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                        />
                      </svg>
                    ) : (
                      idx + 1
                    )}
                  </div>
                  {(idx < assignments.length - 1 || jdVisible) && (
                    <div
                      className={`w-0.5 h-6 mt-1 ${isPassed ? 'bg-accent-300 dark:bg-accent-800' : 'bg-slate-200 dark:bg-slate-700'}`}
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge color={color === 'blue' ? 'blue' : 'red'} className="text-[10px]">
                      {typeShortLabel(type, t)}
                    </Badge>
                    <StatusBadge status={training?.status || 'NOT_STARTED'} />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-0.5">
                    {TYPE_LABELS[type] ? t(TYPE_LABELS[type]) : assignment.course.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                    {assignment.course.title}
                  </p>

                  {/* Bară de progres pe module — vizibilă cât timp instruirea e în curs și are module.
                      Refolosește getModuleProgress (aceeași regulă ca backend-ul). */}
                  {isInProgress &&
                    (assignment.course.modules?.length || 0) > 0 &&
                    (() => {
                      const prog = getModuleProgress(training, assignment.course.modules);
                      return (
                        <div className="mt-2 max-w-xs">
                          <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                            <span>
                              {t('compliance.moduleProgress', {
                                done: prog.completed,
                                total: prog.total,
                              })}
                            </span>
                            <span className="tabular-nums">{prog.percent}%</span>
                          </div>
                          <ProgressBar
                            completed={prog.completed}
                            total={prog.total}
                            percent={prog.percent}
                          />
                        </div>
                      );
                    })()}

                  {/* Score if passed */}
                  {isPassed && training.score != null && (
                    <p className="text-xs text-accent-600 dark:text-accent-300 font-medium mt-1">
                      {t('myCourses.score')}: {training.score}%
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {!unlocked ? (
                      <span className="text-xs text-slate-400 dark:text-slate-400">
                        {t('myCourses.finishPrevious')}
                      </span>
                    ) : isPassed && hasSigned ? null : isPassed ? ( // Completat și semnat — nimic de făcut
                      <>
                        {!hasSigned && (
                          <Link to={`/training/${training.id}/sign`}>
                            <Button variant="accent" size="sm">
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                              {t('myCourses.sign')}
                            </Button>
                          </Link>
                        )}
                        {/* Continuitate cursivă: pe ultimul curs al grupului, dacă există FP încă ne-semnată,
                            butonul „Continuă" duce la fișa postului în loc de următorul curs (din alt grup).
                            Pentru cursurile intermediare păstrează comportamentul normal (curs → curs). */}
                        {hasSigned &&
                          (() => {
                            const isLastCourseInGroup = idx === assignments.length - 1;
                            const fpAvailable =
                              isLastCourseInGroup &&
                              jdVisible &&
                              jobDescriptionStep?.instanceId &&
                              ['WAITING_EMPLOYEE', 'WAITING_COUNTERPART', 'FULLY_SIGNED'].includes(
                                jobDescriptionStep.status
                              ) &&
                              jobDescriptionStep.status !== 'FULLY_SIGNED';
                            if (fpAvailable) {
                              return (
                                <Link
                                  to={`/job-descriptions/${jobDescriptionStep.instanceId}/sign`}
                                >
                                  <Button variant="primary" size="sm">
                                    {t('myCourses.continueToJobDescription', {
                                      domain: jobDescriptionStep.domain,
                                      defaultValue: `Continuă la Fișa postului ${jobDescriptionStep.domain}`,
                                    })}{' '}
                                    →
                                  </Button>
                                </Link>
                              );
                            }
                            if (nextCourse) {
                              return (
                                <Link to={`/training/${nextCourse.course.id}`}>
                                  <Button variant="primary" size="sm">
                                    {t('myCourses.continueToNext', {
                                      type: typeShortLabel(nextCourse.course.type, t),
                                      service: isSUType(nextCourse.course.type) ? 'SU' : 'SSM',
                                    })}{' '}
                                    →
                                  </Button>
                                </Link>
                              );
                            }
                            return null;
                          })()}
                      </>
                    ) : isFailed ? (
                      <Link to={trainingLink}>
                        <Button variant="primary" size="sm">
                          {t('myCourses.retakeTest')}
                        </Button>
                      </Link>
                    ) : (
                      <Link to={trainingLink}>
                        <Button variant="primary" size="sm">
                          {isInProgress
                            ? t('myCourses.continueTraining')
                            : t('myCourses.startTraining')}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Step extra: fișa postului — apare ca al N+1-lea card cu același styling de stepper.
            Materializare lazy în backend (când ILM/IIG corespunzător e complet). */}
        {jdVisible &&
          (() => {
            const stepIdx = assignments.length;
            const isJdSigned = jobDescriptionStep.status === 'FULLY_SIGNED';
            const isJdWaitingEmp = jobDescriptionStep.status === 'WAITING_EMPLOYEE';
            const isJdWaitingCp = jobDescriptionStep.status === 'WAITING_COUNTERPART';
            const isJdTemplateMissing = jobDescriptionStep.status === 'TEMPLATE_MISSING';
            const domain = jobDescriptionStep.domain;

            return (
              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        isJdSigned
                          ? 'bg-accent-100 dark:bg-accent-950/40 text-accent-700 dark:text-accent-300'
                          : isJdWaitingEmp
                            ? `${color === 'blue' ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 ring-2 ring-primary-300 dark:ring-primary-800' : 'bg-danger-100 dark:bg-warning-900/40 text-danger-700 dark:text-warning-300 ring-2 ring-danger-300 dark:ring-warning-800'}`
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {isJdSigned ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        stepIdx + 1
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge color={color === 'blue' ? 'blue' : 'red'} className="text-[10px]">
                        FP
                      </Badge>
                      {isJdSigned && (
                        <Badge color="green" className="text-[10px]">
                          {t('myCourses.completed')}
                        </Badge>
                      )}
                      {isJdWaitingEmp && (
                        <Badge color="yellow" className="text-[10px]">
                          {t('jobDescription.statusWaitingEmployee')}
                        </Badge>
                      )}
                      {isJdWaitingCp && (
                        <Badge color="blue" className="text-[10px]">
                          {t('jobDescription.statusWaitingAdmin')}
                        </Badge>
                      )}
                      {isJdTemplateMissing && (
                        <Badge color="red" className="text-[10px]">
                          {t('jobDescription.statusTemplateMissing')}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-0.5">
                      {t('jobDescription.cardTitle', {
                        domain,
                        defaultValue: `Fișa postului — ${domain}`,
                      })}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                      {t('jobDescription.bilingualBadge', 'Bilingv RO/EN')} ·{' '}
                      {domain === 'SSM'
                        ? t('myCourses.domainSSMFull')
                        : t('myCourses.domainSUFull')}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {isJdWaitingEmp && (
                        <Link to={`/job-descriptions/${jobDescriptionStep.instanceId}/sign`}>
                          <Button variant="accent" size="sm">
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                            {t('jobDescription.signNow', 'Semnează acum')}
                          </Button>
                        </Link>
                      )}
                      {!isJdTemplateMissing && jobDescriptionStep.instanceId && (
                        <Link to={`/job-descriptions/${jobDescriptionStep.instanceId}/sign`}>
                          <Button variant="ghost" size="sm">
                            {t('jobDescription.viewDocument', 'Vezi documentul')}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
      </div>
    </div>
  );
};

// Card simplu pentru cursuri periodice/suplimentare
const CourseCard = ({ assignment, unlocked, blockers = [], _navigate }) => {
  const { t } = useTranslation();
  const training = assignment.training;
  const isPassed = training?.status === 'PASSED';
  const isFailed = training?.status === 'FAILED';
  const isInProgress = training?.status === 'IN_PROGRESS';
  const hasSigned = training?.signatures?.some((s) => s.signerRole === 'EMPLOYEE');
  const type = assignment.course.type;
  const trainingLink = `/training/${assignment.course.id}`;
  const borderColor = isSSMType(type)
    ? 'border-l-primary-500 dark:border-l-primary-600'
    : 'border-l-danger-500 dark:border-l-warning-600';

  // Deadline: dacă atribuirea are dueDate și cursul nu e încă promovat, afișăm badge cu
  // urgență vizuală: roșu dacă au mai rămas ≤3 zile sau e depășit, amber pentru ≤14 zile,
  // gri pentru orizont mai lung. Anterior dueDate se salva în backend dar nu se vedea nicăieri
  // în UI-ul angajatului.
  const deadlineInfo = (() => {
    if (!assignment.dueDate || isPassed) return null;
    const due = new Date(assignment.dueDate);
    const now = new Date();
    const days = Math.ceil((due - now) / 86400000);
    let color = 'slate';
    let label;
    if (days < 0) {
      color = 'red';
      label = t('myCourses.deadlineOverdue', { count: -days });
    } else if (days <= 3) {
      color = 'red';
      label = t('myCourses.deadlineUrgent', { count: days });
    } else if (days <= 14) {
      color = 'yellow';
      label = t('myCourses.deadlineSoon', { count: days });
    } else {
      color = 'slate';
      label = t('myCourses.deadlineDate', { date: due.toLocaleDateString('ro-RO') });
    }
    return { color, label, days };
  })();

  return (
    <Card
      hover
      className={`flex flex-col border-l-4 ${borderColor} ${!unlocked ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Badge color={isSSMType(type) ? 'blue' : 'red'}>
            {typeShortLabel(type, t)} {isSSMType(type) ? 'SSM' : 'SU'}
          </Badge>
          {!unlocked && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
              <svg
                className="w-3 h-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              {t('myCourses.locked')}
            </span>
          )}
        </div>
        <StatusBadge status={training?.status || 'NOT_STARTED'} />
      </div>

      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
        {assignment.course.title}
      </h3>
      {assignment.course.description && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 line-clamp-2">
          {assignment.course.description}
        </p>
      )}
      {deadlineInfo && (
        <div className="mb-2">
          <Badge color={deadlineInfo.color} className="text-[11px] font-medium">
            ⏰ {deadlineInfo.label}
          </Badge>
        </div>
      )}

      {/* Bară de progres pe module — vizibilă cât timp instruirea e în curs și are module. */}
      {isInProgress &&
        (assignment.course.modules?.length || 0) > 0 &&
        (() => {
          const prog = getModuleProgress(training, assignment.course.modules);
          return (
            <div className="mb-3">
              <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 mb-1">
                <span>
                  {t('compliance.moduleProgress', { done: prog.completed, total: prog.total })}
                </span>
                <span className="tabular-nums">{prog.percent}%</span>
              </div>
              <ProgressBar completed={prog.completed} total={prog.total} percent={prog.percent} />
            </div>
          );
        })()}

      {/* Mesaj blocaj — listează ce prerequisite lipsesc. Vizibil DOAR pentru IP/SUPLIMENTARE
          când unlocked=false. Aliniat cu mesajul backend din `getCrossTrainingBlockers`. */}
      {!unlocked && blockers.length > 0 && (
        <div className="mb-3 p-2 rounded-md bg-warning-50 dark:bg-warning-950/30 border border-warning-200 dark:border-warning-700">
          <p className="text-[10px] font-semibold text-warning-800 dark:text-warning-200 mb-0.5 uppercase tracking-wide">
            {t('myCourses.prerequisites')}
          </p>
          <ul className="text-xs text-warning-700 dark:text-warning-300 space-y-0.5">
            {blockers.map((b, i) => (
              <li key={i}>• {b}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto flex items-center gap-2">
        {isPassed ? (
          <>
            <span className="text-xs text-accent-600 dark:text-accent-300 font-medium">
              {training.score != null
                ? `${t('myCourses.score')}: ${training.score}%`
                : t('myCourses.completed')}
            </span>
            {!hasSigned && (
              <Link to={`/training/${training.id}/sign`} className="ml-auto">
                <Button variant="accent" size="sm">
                  {t('myCourses.sign')}
                </Button>
              </Link>
            )}
          </>
        ) : isFailed ? (
          <Link to={trainingLink} className="w-full">
            <Button variant="primary" size="sm" className="w-full">
              {t('myCourses.retakeTest')}
            </Button>
          </Link>
        ) : !unlocked ? (
          // IP/SUPLIMENTARE blocat — buton dezactivat, fără link
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            disabled
            title={t('myCourses.lockedTooltip')}
          >
            {t('myCourses.lockedCta')}
          </Button>
        ) : (
          <Link to={trainingLink} className="w-full">
            <Button variant="primary" size="sm" className="w-full">
              {isInProgress ? t('myCourses.continueTraining') : t('myCourses.startTraining')}
            </Button>
          </Link>
        )}
      </div>
    </Card>
  );
};

export default MyCourses;
